import {
  ChangeEvent,
  ForwardedRef,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { FilledInputProps } from '@mui/material';
import BigNumber from 'bignumber.js';

import { useStyles } from 'modules/common/components/NumberTextField/useStyles';
import {
  ITextFieldProps,
  TextField,
} from 'modules/common/components/TextField';
import { useValidateNumber } from 'modules/common/hooks/useValidateNumber';

export interface INumberTextFieldProps<T extends FieldValues = FieldValues>
  extends ITextFieldProps {
  name: Path<T>;
  control: Control<T>;
  min?: BigNumber;
  unit?: string;
  integer?: boolean;
  shouldValidate?: boolean;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

export const numberRegExp = /^$|^(\d{0,10})(\.\d{0,18})?$/;

function NumberTextFieldComponent<T extends FieldValues>(
  {
    name,
    control,
    balance,
    onChange,
    min,
    unit,
    integer,
    shouldValidate = true,
    InputProps,
    ...props
  }: INumberTextFieldProps<T>,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const { classes, cx } = useStyles();
  const validateAmount = useValidateNumber({
    balance,
    max: balance,
    min,
    unit,
  });
  const { field } = useController({
    name,
    control,
    rules: shouldValidate ? { validate: validateAmount } : undefined,
  });

  const lastValueRef = useRef<string>(field.value);
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const { value } = event.target;

      if (integer && value.includes('.')) {
        return;
      }

      if (numberRegExp.exec(value)) {
        if (onChange) {
          onChange(event);
        }

        if (lastValueRef.current !== value) {
          lastValueRef.current = value;
          field.onChange(value);
        }
      }
    },
    [field, integer, onChange],
  );

  const inputProps: Partial<FilledInputProps> = useMemo(
    () => ({
      ...(InputProps || {}),
      classes: {
        ...(InputProps?.classes || {}),
        input: cx(
          classes.input,
          !!props?.onMaxClick && classes.withPadding,
          InputProps?.classes?.input,
        ),
      },
    }),
    [InputProps, classes.input, classes.withPadding, cx, props?.onMaxClick],
  );

  return (
    <TextField
      ref={ref}
      autoFocus
      autoComplete="off"
      balance={balance}
      unit={unit}
      value={field.value}
      onChange={handleChange}
      {...props}
      InputLabelProps={{ shrink: true }}
      InputProps={inputProps}
    />
  );
}

export const NumberTextField = forwardRef(NumberTextFieldComponent);
